import * as React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { MdEmail, MdPhone, MdEdit, MdAttachFile, MdCalendarMonth, MdPerson, MdDownload, MdNotes, MdVisibility, MdCategory } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { APPRoutes } from '../../Utils/Route';
import { getHealthcarePayorById, getAllHealthcarePayors, previewProviderFile, downloadProviderFile, getAllHealthCareNotes } from '../../Services/ProviderService';
import FileActions from '../../Components/Common/FileActions';
import ViewEmptyState from '../../Components/Common/ViewEmptyState';
import { formatUtcToLocalDateTime } from '../../Utils/DateTime';
import BackButton from '../../Components/Common/BackButton';

const STATUS_STYLE = {
  Completed: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text-strong)' },
  Pending: { backgroundColor: 'var(--hc-status-pending-list-bg)', color: 'var(--hc-icon-amber-text)' },
  Unknown: { backgroundColor: 'var(--hc-status-unknown-bg)', color: 'var(--hc-status-unknown-text)' },
};



const ViewSkeleton = () => (
  <div className="container-fluid py-4 hc-page-bg min-vh-100">
    <div className="d-flex align-items-center justify-content-between mb-3 gap-2">
      <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 40, width: 140, borderRadius: 999 }} />
      <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 40, width: 120, borderRadius: 12 }} />
    </div>

    <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
      <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
      <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />
      <div className="row g-4 align-items-center position-relative">
        <div className="col-lg-8">
          <div className="d-flex flex-wrap gap-2 mb-3">
            <div className="skeleton-hero position-relative overflow-hidden rounded-2" style={{ height: 28, width: 100, borderRadius: 999 }} />
            <div className="skeleton-hero position-relative overflow-hidden rounded-2" style={{ height: 28, width: 100, borderRadius: 999 }} />
          </div>
          <div className="skeleton-hero mb-3" style={{ height: 36, width: '65%', borderRadius: 12 }} />
          <div className="skeleton-hero mb-2 rounded-3" style={{ height: 18, width: '45%' }} />
          <div className="skeleton-hero mb-3 rounded-3" style={{ height: 18, width: '35%' }} />
          <div className="row g-3 mt-3">
            <div className="col-sm-4">
              <div className="skeleton-hero position-relative overflow-hidden rounded-2" style={{ height: 72, borderRadius: 16 }} />
            </div>
            <div className="col-sm-4">
              <div className="skeleton-hero position-relative overflow-hidden rounded-2" style={{ height: 72, borderRadius: 16 }} />
            </div>
            <div className="col-sm-4">
              <div className="skeleton-hero position-relative overflow-hidden rounded-2" style={{ height: 72, borderRadius: 16 }} />
            </div>
          </div>
        </div>
        <div className="col-lg-4 text-center">
          <div className="skeleton-hero position-relative overflow-hidden rounded-circle mx-auto" style={{ width: 130, height: 130 }} />
        </div>
      </div>
    </div>

    <div className="row g-4 mb-4">
      <div className="col-12 col-lg-7">
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 120, borderRadius: 20 }} />
          </div>
          <div className="col-12 col-md-6">
            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 120, borderRadius: 20 }} />
          </div>
          <div className="col-12 col-md-6">
            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 120, borderRadius: 20 }} />
          </div>
          <div className="col-12 col-md-6">
            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 120, borderRadius: 20 }} />
          </div>
          <div className="col-12">
            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 140, borderRadius: 20 }} />
          </div>
        </div>
      </div>
      <div className="col-12 col-lg-5">
        <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 320, borderRadius: 24 }} />
      </div>
    </div>

    <div className="row g-4">
      <div className="col-12 col-xl-4">
        <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 120, borderRadius: 20 }} />
      </div>
      <div className="col-12 col-xl-4">
        <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 120, borderRadius: 20 }} />
      </div>
      <div className="col-12 col-xl-4">
        <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 120, borderRadius: 20 }} />
      </div>
    </div>

    <div className="row g-4 mt-4">
      <div className="col-12 col-lg-7">
        <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 240, borderRadius: 24 }} />
      </div>
      <div className="col-12 col-lg-5">
        <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 240, borderRadius: 24 }} />
      </div>
      <div className="col-12">
        <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 160, borderRadius: 24 }} />
      </div>
    </div>
  </div>
);

const SectionCard = ({ title, icon: Icon, children }) => (
  <div className="p-4 p-lg-5 hc-surface hc-card-primary mb-4 position-relative overflow-hidden shadow-sm rounded-4">
    <div className="d-flex align-items-center gap-2 mb-4">
      <span className="d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: 38, height: 38, backgroundColor: 'rgba(var(--hc-accent-blue-rgb), 0.08)', color: 'var(--hc-accent-blue)' }}>
        <Icon size={18} />
      </span>
      <h5 className="mb-0 fw-bold hc-text-primary">{title}</h5>
    </div>
    {children}
  </div>
);

const DetailInfoCard = ({ label, value, actions }) => (
  <div className="col-12 col-md-6 col-xl-4 mb-3">
    <div className="h-100 rounded-4 border border-light-subtle bg-light px-3 py-3">
      <div className="small mb-1 w-100 hc-text-primary fw-semibold text-break">{label}</div>
      {actions ? actions : <div className="fw-medium text-dark text-break">{value || '-'}</div>}
    </div>
  </div>
);

const DocumentBadge = ({ item }) => {
  if (!item?.file_id && !item?.file_name) return null;
  return (
    <span className="d-inline-flex align-items-center gap-2 rounded-pill border px-3 py-1 mt-2" style={{ color: 'var(--hc-accent-blue)', borderColor: 'rgba(var(--hc-accent-blue-rgb), 0.2)', backgroundColor: 'rgba(var(--hc-accent-blue-rgb), 0.06)' }}>
      <MdAttachFile size={14} />
      {item.file_name || 'Document attached'}
    </span>
  );
};

export default function HealthcarePayorsView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken]
  );
  const canAccessPage = permissionHas(userPermissionCodes, PermissionCodes.HEALTHCARE_PAYOR_VIEW, decodedToken);
  const canEditPayor = permissionHas(userPermissionCodes, PermissionCodes.HEALTHCARE_PAYOR_EDIT, decodedToken);
  const canViewList = permissionHas(userPermissionCodes, PermissionCodes.HEALTHCARE_PAYOR_LIST, decodedToken);

  const payorFromState = React.useMemo(() => {
    const s = (location && location.state) ? location.state : null;
    if (!s || typeof s !== 'object') return null;
    return s.payor || s.provider || s.item || s.data || s;
  }, [location?.state]);
  const [payor, setPayor] = React.useState(payorFromState);
  const [loading, setLoading] = React.useState(!payorFromState);
  
  const [downloadingId, setDownloadingId] = React.useState(null);
  const [notes, setNotes] = React.useState([]);
  const [notesLoading, setNotesLoading] = React.useState(false);
  const [notesPage, setNotesPage] = React.useState(0);
  const NOTES_PER_PAGE = 3;

  const createdOnLocal = React.useMemo(() => formatUtcToLocalDateTime(payor?.created_on), [payor?.created_on]);
  const modifiedOnLocal = React.useMemo(() => formatUtcToLocalDateTime(payor?.modified_on), [payor?.modified_on]);

  const loadPayor = React.useCallback(async () => {
    if (!token) return;
    let payorId =
      id ||
      payorFromState?.id ||
      payorFromState?.Id ||
      payorFromState?.payor_id ||
      payorFromState?.providerId ||
      payorFromState?.provider_id ||
      null;
    try {
      setLoading(true);
      if (!payorId || payorId === 'first' || payorId === ':id') {
        const listRes = await getAllHealthcarePayors(token);
        const rawList = listRes?.data?.payors || listRes?.data?.Payors || listRes?.data || listRes;
        const list = Array.isArray(rawList) ? rawList : [];
        payorId = list[0]?.id || list[0]?.Id || null;
      }
      if (!payorId) {
        setPayor(null);
        return;
      }
      const res = await getHealthcarePayorById(token, payorId);
      if (res?.is_success) {
        setPayor(res?.data ?? res);
      } else {
        toast.error(res?.message || 'Failed to load payor');
        setPayor(null);
      }
    } catch (err) {
      console.error('Error loading payor', err);
      toast.error('Failed to load payor');
      setPayor(null);
    } finally {
      setLoading(false);
    }
  }, [token, id, payorFromState]);

  const sortedNotes = React.useMemo(() => {
    if (!notes?.length) return [];
    return [...notes].sort((a, b) => new Date(b.utc) - new Date(a.utc));
  }, [notes]);

  const notesTotalPages = Math.max(Math.ceil(sortedNotes.length / NOTES_PER_PAGE), 1);
  const visibleNotes = sortedNotes.slice(notesPage * NOTES_PER_PAGE, notesPage * NOTES_PER_PAGE + NOTES_PER_PAGE);

  const loadHealthCareNotes = React.useCallback(async () => {
    if (!token || !(payor?.id || payor?.healthcarepayor_id)) return;
    setNotesLoading(true);
    try {
      const payorId = payor?.id || payor?.healthcarepayor_id;
      const res = await getAllHealthCareNotes(token, payorId);
      const notesData =
        Array.isArray(res?.data) && res.data.length
          ? res.data[0]?.notes ?? []
          : [];
      setNotes(notesData);
      setNotesPage(0);
    } catch (err) {
      console.error('Error loading healthcare notes:', err);
      toast.error('Failed to load healthcare notes');
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  }, [token, payor?.id, payor?.healthcarepayor_id]);

  React.useEffect(() => {
    if (!token) return;
    if (payor?.id || payor?.healthcarepayor_id) loadHealthCareNotes();
  }, [token, payor?.id, payor?.healthcarepayor_id, loadHealthCareNotes]);

  React.useEffect(() => {
    if (!token) return;
    if (!payorFromState) loadPayor();
  }, [token, payorFromState, loadPayor]);

  if (!canAccessPage) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger">You do not have permission to view healthcare payors.</div>
      </div>
    );
  }

  const renderFileActionButtons = (item) => {
    return (
      <FileActions
        fileId={item?.file_id}
        fileName={item?.file_name}
        className="w-100"
        onDownload={async (token, fid) => {
          const res = await downloadProviderFile(token, fid);
          return res;
        }}
        onPreview={async (token, fid) => {
          const res = await previewProviderFile(token, fid);
          return res;
        }}
      />
    );
  };

  const organizationNameLabel = payor && payor.organization_name ? String(payor.organization_name) : '-';
  const organizationNamePreview = organizationNameLabel && organizationNameLabel.length > 25 ? `${organizationNameLabel.slice(0, 25).trim()}...` : organizationNameLabel;

  

  if (loading) {
    return <ViewSkeleton />;
  }

  if (!payor) {
    return (
      <ViewEmptyState
        title="No Healthcare Payors Available"
        description="There are no healthcare payor records available to display."
        icon={MdCategory}
        canViewList={canViewList}
        backRoute={APPRoutes.HEALTHCARE_PAYOR}
        backLabel="Back to Healthcare Payors"
      />
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="d-flex align-items-center justify-content-between mb-3 gap-2">
        <div className="d-flex align-items-center gap-3">
          {canViewList && (
            <BackButton onClick={() => navigate(APPRoutes.HEALTHCARE_PAYOR)} />
          )}
        </div>
        <div>
          {canEditPayor ? (
            <button
              type="button"
              className="btn hc-btn-next border-0 d-inline-flex align-items-center gap-2"
              onClick={() => navigate(APPRoutes.HEALTHCARE_PAYOR_EDIT.replace(':id', payor.id))}
            >
              <MdEdit size={18} /> Edit Payor
            </button>
          ) : null}
        </div>
      </div>

      <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
        <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
        <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />
        <div className="row g-4 align-items-center position-relative">
          <div className="col-lg-8">
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              {payor.payor_status_name ? (
                <span
                  className="px-3 py-2 rounded-pill fw-semibold"
                  style={{
                    fontSize: '0.82rem',
                    backgroundColor: STATUS_STYLE[payor.payor_status_name]?.backgroundColor || 'rgba(var(--hc-white-rgb), 0.18)',
                    color: STATUS_STYLE[payor.payor_status_name]?.color || 'var(--hc-white)',
                  }}
                >
                  {payor.payor_status_name}
                </span>
              ) : null}
              {payor.par_status_name ? (
                <span className="px-3 py-2 rounded-pill" style={{ fontSize: '0.82rem', backgroundColor: 'rgba(var(--hc-white-rgb), 0.10)', color: 'var(--hc-white)' }}>
                  {payor.par_status_name}
                </span>
              ) : null}
            </div>

            <h2 className="fw-bold text-white mb-2">{payor.payor_name || '-'}</h2>
            <p className="mb-1" style={{ color: 'rgba(var(--hc-white-rgb), 0.78)', fontSize: '0.95rem' }}>
              <MdEmail size={14} className="me-1" />{payor.contact?.contact_email || '-'}
            </p>
            <p className="mb-0" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)', fontSize: '0.88rem' }}>
              <MdPhone size={14} className="me-1" />{payor.contact?.contact_phone || '-'}
            </p>

            <div className="row g-3 mt-0">
              <div className="col-sm-6 col-xl-4">
                <div className="rounded-4 p-3" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.08)' }}>
                  <div className="small text-white-50">Payor State</div>
                  <div className="fw-semibold text-white">{payor.payor_state || '-'}</div>
                </div>
              </div>
              <div className="col-sm-6 col-xl-4">
                <div className="rounded-4 p-3" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.08)' }}>
                  <div className="small text-white-50">Effective</div>
                  <div className="fw-semibold text-white">{payor.effective_date || '-'}</div>
                </div>
              </div>
              <div className="col-sm-6 col-xl-4">
                <div className="rounded-4 p-3" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.08)' }}>
                  <div className="small text-white-50">End Date</div>
                  <div className="fw-semibold text-white">{payor.end_date || '-'}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="p-4 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.12)' }}>
              <div className="d-flex align-items-center gap-3 mb-3">
                <div className="d-flex align-items-center justify-content-center overflow-hidden rounded-3 flex-shrink-0" style={{ width: 56, height: 56, background: 'rgba(var(--hc-white-rgb), 0.15)' }}>
                  <span className="fw-bold text-white" style={{ fontSize: '1.3rem', letterSpacing: '0.06em' }}>{(payor.payor_name || 'P').slice(0, 2).toUpperCase()}</span>
                </div>
                <div>
                  <p className="mb-0 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>Organization</p>
                  <p className="fw-bold text-white mb-0 mw-100" style={{ fontSize: '1.1rem', lineHeight: 1.1, wordBreak: 'break-word', overflowWrap: 'break-word' }} title={organizationNameLabel}>
                    {organizationNamePreview}
                  </p>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-12">
                  <div className="rounded-4 p-3 py-2" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.08)' }}>
                    <div className="small text-white-50">Provider</div>
                    <div className="fw-semibold text-white">{payor.provider_name || `${payor.provider?.first_name || ''} ${payor.provider?.last_name || ''}`.trim() || '-'}</div>
                  </div>
                  <p className="mb-0 mt-3 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.72)' }}>
                    Displays the payor record and its associated details for review.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-12">
          <SectionCard title="Payor Information" icon={MdCalendarMonth}>
            <div className="row">
              <DetailInfoCard label="Provider" value={payor.provider_name || `${payor.provider?.first_name || ''} ${payor.provider?.last_name || ''}`.trim()} />
              <DetailInfoCard label="PAR Status" value={payor.par_status_name} />
              <DetailInfoCard label="Speciality" value={payor.speciality || payor.Speciality} />
              <DetailInfoCard label="Practice" value={payor.practice} />
              <DetailInfoCard label="Category / Plan" value={payor.category_plan} />
              <DetailInfoCard label="Payor State" value={payor.payor_state} />
              <DetailInfoCard label="Effective Date" value={payor.effective_date} />
              <DetailInfoCard label="End Date" value={payor.end_date} />
              <DetailInfoCard label="Revalidation Date" value={payor.revalidation_date} />
              <DetailInfoCard label="Application Submission Date" value={payor.application_submission_date} />
              <DetailInfoCard label="Application Resubmission Date" value={payor.application_resubmission_date} />
              <DetailInfoCard label="Approval Date" value={payor.approval_date} />
              {payor.file_id ? (
                <DetailInfoCard
                  label={`File - ${payor.file_name || 'Attachment'}`}
                  actions={renderFileActionButtons(payor)}
                />
              ) : null}
              <div className="col-12 mb-3">
                <div className="h-100 rounded-4 border border-light-subtle bg-light px-3 py-3">
                  <div className="small mb-1 w-100 hc-text-primary fw-semibold">Description</div>
                  <div className="fw-medium text-dark" style={{ whiteSpace: 'pre-wrap' }}>{payor.payor_description || '-'}</div>
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Notes" icon={MdNotes}>
            {notesLoading ? (
              <div className="row g-3">
                {Array.from({ length: NOTES_PER_PAGE }).map((_, idx) => (
                  <div key={idx} className="col-12">
                    <div className="h-100 rounded-4 border border-light-subtle bg-light px-3 py-3">
                      <div className="skeleton mb-2 rounded-2" style={{ height: 14, width: '35%' }} />
                      <div className="skeleton mb-2 rounded-2" style={{ height: 12, width: '20%' }} />
                      <div className="skeleton mb-2 rounded-2" style={{ height: 10, width: '90%' }} />
                      <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 10, width: '80%' }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="row g-3">
                  {visibleNotes.length ? (
                    visibleNotes.map((note, index) => (
                      <div key={`${note.utc}-${index}`} className="col-12">
                        <div className="h-100 rounded-4 border border-light-subtle bg-light px-3 py-3">
                          <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
                            <span className="small text-dark fw-semibold">{note.by || '-'}</span>
                            <span className="small text-muted">{formatUtcToLocalDateTime(note.utc) || '-'}</span>
                          </div>
                          <div className="small text-dark" style={{ whiteSpace: 'pre-wrap' }}>{note.msg || '-'}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-12">
                      <div className="h-100 rounded-4 border border-light-subtle bg-light px-3 py-4 text-center text-muted">
                        No notes available.
                      </div>
                    </div>
                  )}
                </div>
                {sortedNotes.length > NOTES_PER_PAGE && (
                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <button
                      type="button"
                      className="btn hc-btn-prev btn-sm"
                      onClick={() => setNotesPage((prev) => Math.max(prev - 1, 0))}
                      disabled={notesPage === 0}
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setNotesPage((prev) => Math.min(prev + 1, notesTotalPages - 1))}
                      disabled={notesPage >= notesTotalPages - 1}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </SectionCard>
          <SectionCard title="Contact Information" icon={MdPerson}>
            <div className="row">
              <DetailInfoCard label="Contact Title" value={payor.contact?.contact_title} />
              <DetailInfoCard label="Contact Name" value={payor.contact?.contact_name} />
              <DetailInfoCard label="Contact Email" value={payor.contact?.contact_email} />
              <DetailInfoCard label="Contact Phone" value={payor.contact?.contact_phone} />
              <DetailInfoCard label="Contact Fax" value={payor.contact?.contact_fax} />
            </div>
          </SectionCard>

          
        </div>
      </div>

      <div className="p-4 p-lg-5 hc-surface hc-card-primary">
        <h5 className="fw-bold mb-3 d-flex align-items-center gap-2 hc-text-primary">
          <MdCalendarMonth size={22} className="hc-text-primary" /> Audit Info
        </h5>

        <div className="row g-3">
          <div className="col-12 col-md-6">
            <div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
              <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
                <MdPerson size={18} className="hc-text-primary" />
              </span>
              <div className="text-start">
                <div className="small text-muted">Created By</div>
                <div className="fw-medium text-dark">{payor.created_by_name || '-'}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
              <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
                <MdCalendarMonth size={18} className="hc-text-primary" />
              </span>
              <div className="text-start">
                <div className="small text-muted">Created On</div>
                <div className="fw-medium text-dark">{createdOnLocal || '-'}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
              <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
                <MdPerson size={18} className="hc-text-primary" />
              </span>
              <div className="text-start">
                <div className="small text-muted">Modified By</div>
                <div className="fw-medium text-dark">{payor.modified_by_name || '-'}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
              <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
                <MdCalendarMonth size={18} className="hc-text-primary" />
              </span>
              <div className="text-start">
                <div className="small text-muted">Modified On</div>
                <div className="fw-medium text-dark">{modifiedOnLocal || '-'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
